import { api, createApiQuery, fetchApi } from "@/lib/api";
import {
  type components,
  createOrganizationMembersHandlers,
  generateId,
  type MemberManagementActions as OrganizationMembersActions,
  type OrganizationRole,
  queryKeys,
} from "@stockpilot/shared";
import { useSession } from "@/lib/auth";

export function useOrganizationMembers(organizationId: () => string | undefined) {
  const session = useSession();

  // P23 · I1 — goes through `createApiQuery` (the chokepoint that already applies
  // `handleApiResponse`) instead of raw `createQuery`. `createQuery` is also the
  // DEPRECATED ALIAS of `useQuery` in solid-query v5, so this was exactly the place
  // a guard written only against `useQuery` would not have seen.
  const members = createApiQuery<
    components["schemas"]["OrganizationMemberDTO"][],
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.members(organizationId() ?? "none"),
    async () =>
      api.GET("/api/org/listMembers", {
        params: { query: { organizationId: organizationId() ?? "" } },
      }),
    () => ({ enabled: Boolean(session.data) && Boolean(organizationId()) }),
  );

  const actions: OrganizationMembersActions<OrganizationRole> = {
    inviteMember: async (email: string, role: OrganizationRole) => {
      const orgId = organizationId();
      if (!orgId) {
        return;
      }
      await fetchApi(
        api.POST("/api/org/inviteMember", {
          body: {
            id: generateId(),
            organizationId: orgId,
            email,
            role,
          },
        }),
      );
      await members.refetch();
    },
    removeMember: async (memberIdOrUserId: string) => {
      const orgId = organizationId();
      if (!orgId) {
        return;
      }
      await fetchApi(
        api.POST("/api/org/removeMember", {
          body: {
            id: memberIdOrUserId,
          },
        }),
      );
      await members.refetch();
    },
    updateMemberRole: async (memberId: string, role: OrganizationRole) => {
      const orgId = organizationId();
      if (!orgId) {
        return;
      }
      await fetchApi(
        api.POST("/api/org/updateMemberRole", {
          body: {
            id: memberId,
            role,
          },
        }),
      );
      await members.refetch();
    },
  };

  const {
    handleInviteMember: inviteMember,
    handleRemoveMember: removeMember,
    handleUpdateMemberRole: updateMemberRole,
  } = createOrganizationMembersHandlers(actions);

  return {
    // P23 / CC-60 · I2 — `?? []` without a guard is a LIE: if `/api/org/listMembers`
    // is down, the organization renders as "without members", and the permissions
    // the user believes are assigned depend on that list. `isSuccess` is the exact
    // condition for the existence of `data`, so it does not change any healthy case.
    members: () => (members.isSuccess ? (members.data ?? []) : []),
    isError: () => members.isError,
    refetch: () => void members.refetch(),
    isLoading: () => members.isLoading,
    inviteMember,
    removeMember,
    updateMemberRole,
  };
}
